import { generateUUID } from '../../../utils/uuid';
import { LTT_STATUS, LOTO_CHECKLIST_QUESTIONS, PPE_OPTIONS, EMERGENCY_ARRANGEMENTS } from './constants';

/**
 * Creates a new Lock-Tag-Try Permit record skeleton with all required fields
 */
export function createLttSkeleton(overrides = {}) {
    const now = new Date().toISOString();

    // Initialize LOTO checklist answers (all null initially)
    const lotoAnswers = {};
    LOTO_CHECKLIST_QUESTIONS.forEach(q => {
        lotoAnswers[q.id] = null; // null = not answered, 'yes', 'no', 'na'
    });

    // Initialize PPE selections (all false initially)
    const ppeSelections = {};
    PPE_OPTIONS.forEach(ppe => {
        ppeSelections[ppe.id] = false;
    });

    // Initialize emergency arrangements (all false initially)
    const emergencyArrangements = {};
    EMERGENCY_ARRANGEMENTS.forEach(item => {
        emergencyArrangements[item.id] = false;
    });

    return {
        uuid: generateUUID(),
        status: LTT_STATUS.NEW,
        createdAt: now,
        updatedAt: now,

        // Header Section
        header: {
            mode: 'job', // 'job' or 'location'
            jobId: null,
            jobCode: '',
            jobName: '',
            location: '',
            workCarriedOutBy: '',
            workCarriedOutById: null,
            workCarriedOutByType: 'employee', // 'employee' or 'temp'
            hasCategory3Multimeter: false, // Category III 600V Multimeter checkbox
            equipment: [], // Array of strings (manually entered equipment)
            descriptionOfWork: ''
        },

        // Hazard Notes (simple text field with dictation)
        hazardNotes: '',

        // Permit Duration (ISO datetime strings)
        permitDuration: {
            startDateTime: null,
            endDateTime: null
        },

        // LOTO Checklist
        lotoChecklist: {
            answers: lotoAnswers
        },

        // Control Measures
        controlMeasures: {
            arcFlashPpeCategory: '',
            noLabelCategory4Required: false,
            notes: ''
        },

        // Additional PPE
        additionalPpe: {
            selections: ppeSelections,
            customPpe: [] // Array of strings for custom PPE items
        },

        // Emergency Arrangements
        emergencyArrangements: {
            selections: emergencyArrangements,
            nearestEmergencyLocation: '',
            emergencyContact: ''
        },

        // Workers (kept for backward compat with old records)
        workers: [],

        // V2: Lockout Log - replaces workers for new records
        // Array of { lockTagNumber, name, phone, dateApplied, releasedDate, releasedSignature }
        lockoutLog: [],

        // V2: Verify Zero Energy notes
        verifyZeroEnergyNotes: '',

        // V2: Control Measures Method text
        controlMeasuresMethod: '',

        // Signatures (V2: 2 sections - checklistInitiator + completionVerification)
        signatures: {
            checklistInitiator: {
                name: '',
                date: null,
                signature: null,
                signedById: null,
                signedByType: 'employee'
            },
            completionVerification: {
                name: '',
                date: null,
                signature: null,
                signedById: null,
                signedByType: 'employee',
                completionStatus: null // 'completed' or 'partiallyCompleted'
            }
        },

        // Metadata
        meta: {
            submittedAt: null,
            submittedBy: null,
            submittedByName: null
        },

        ...overrides
    };
}

/**
 * Ensures all fields have proper structure (for migrations/updates)
 */
export function ensureLttStructure(record) {
    const skeleton = createLttSkeleton();
    // Preserve original uuid - never generate new one for existing records
    const uuid = record.uuid || skeleton.uuid;

    return {
        ...skeleton,
        ...record,
        uuid, // Explicitly preserve uuid
        header: {
            ...skeleton.header,
            ...(record.header || {}),
            hasCategory3Multimeter: record.header?.hasCategory3Multimeter || false,
            equipment: record.header?.equipment || []
        },
        hazardNotes: record.hazardNotes || '',
        permitDuration: { ...skeleton.permitDuration, ...(record.permitDuration || {}) },
        lotoChecklist: {
            ...skeleton.lotoChecklist,
            ...(record.lotoChecklist || {}),
            answers: { ...skeleton.lotoChecklist.answers, ...(record.lotoChecklist?.answers || {}) }
        },
        controlMeasures: { ...skeleton.controlMeasures, ...(record.controlMeasures || {}) },
        additionalPpe: {
            ...skeleton.additionalPpe,
            ...(record.additionalPpe || {}),
            selections: { ...skeleton.additionalPpe.selections, ...(record.additionalPpe?.selections || {}) },
            customPpe: record.additionalPpe?.customPpe || []
        },
        emergencyArrangements: {
            ...skeleton.emergencyArrangements,
            ...(record.emergencyArrangements || {}),
            selections: { ...skeleton.emergencyArrangements.selections, ...(record.emergencyArrangements?.selections || {}) }
        },
        workers: record.workers || [],
        lockoutLog: record.lockoutLog || [],
        verifyZeroEnergyNotes: record.verifyZeroEnergyNotes || '',
        controlMeasuresMethod: record.controlMeasuresMethod || '',
        signatures: {
            // V2 signature keys
            checklistInitiator: {
                ...skeleton.signatures.checklistInitiator,
                ...(record.signatures?.checklistInitiator || record.signatures?.permitIssuer || {})
            },
            completionVerification: {
                ...skeleton.signatures.completionVerification,
                ...(record.signatures?.completionVerification || record.signatures?.handBack || {})
            },
            // Keep old keys if they exist (for backward compat)
            ...(record.signatures?.permitIssuer ? { permitIssuer: record.signatures.permitIssuer } : {}),
            ...(record.signatures?.permitAcceptor ? { permitAcceptor: record.signatures.permitAcceptor } : {}),
            ...(record.signatures?.handBack ? { handBack: record.signatures.handBack } : {}),
            ...(record.signatures?.cancellation ? { cancellation: record.signatures.cancellation } : {})
        },
        meta: { ...skeleton.meta, ...(record.meta || {}) }
    };
}
